import { useEffect, useRef, useState } from "react";
import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import * as api from "../api";
import { listSolutions, type SolutieRand } from "../nav/api";
import { areDesen } from "../pages/generare";
import type { CanonicalDoc } from "./canonical";

/** Cât se așteaptă între două cereri ale arhitecturii care încă macină.
 *  Documentul e de ~20 MB și crește pe parcurs; la 30 s omul vede clădirile
 *  umplându-se fără să ținem o copie veche pe ecran, iar traficul rămâne al
 *  celui care chiar se uită la machetă. */
const REIA_ARH_MS = 30000;

/** ARHITECTURA ÎNLĂNȚUITĂ PE UN PLAN DE SITUAȚIE, adusă când se deschide
 *  macheta și reîntrebată cât timp încă macină.
 *
 *  Starea (`docArhBrut`, `arhId`, `arhSeIncarca`, `arhInLucru`) rămâne
 *  declarată în `Vizualizator`, unde era: între ea și efectul de aici stau alte
 *  hook-uri care o citesc, iar ordinea hook-urilor nu se schimbă. */
export function useArhitecturaInlantuita({
  projectId, solution, machetaSit, vedeSituatia, setDocArh, setArhId,
  setArhSeIncarca, setArhInLucru, adoptaCheiaArh, semnalRegistru, laAnulare,
}: {
  projectId: string;
  solution: SolutieRand;
  machetaSit: boolean;
  vedeSituatia: boolean;
  setDocArh: (d: CanonicalDoc | null) => void;
  setArhId: Dispatch<SetStateAction<string | null>>;
  setArhSeIncarca: Dispatch<SetStateAction<boolean>>;
  setArhInLucru: Dispatch<SetStateAction<boolean>>;
  adoptaCheiaArh: MutableRefObject<boolean>;
  /** Ce spune REGISTRUL VIU despre arhitecturile acestui plan (id, stare,
   *  `updated_at`, revizia fațadei — v. `Vizualizator`). Când se schimbă,
   *  arhitectura se caută din nou: una care NU EXISTA la deschiderea machetei
   *  (planul abia scris, etajele vin peste un minut) altfel nu se mai cerea
   *  niciodată — bătaia de 30 s e numai a unei arhitecturi care există și
   *  macină —, iar macheta rămânea pe volumele albe, cu panoul de fațadă fără
   *  arhitectură: fără firul agentului, fără pulsul butonului, fără ce scrie el
   *  (Cătălin, 26.10.2026: «asistentul începe foarte târziu pe anumite
   *  soluții»). Documentul se cere tot numai când cheia lui s-a schimbat. */
  semnalRegistru?: string;
  /** Chemat în locul lui `setArhSeIncarca(false)` când o căutare e ANULATĂ pe
   *  drum (efectul s-a reluat înaintea răspunsului). Pentru cine deosebește
   *  «s-a aflat» de «s-a oprit»: o anulare nu spune nimic despre arhitectură —
   *  v. `intrareMacheta.dupaAnulare`. */
  laAnulare?: () => void;
}) {
  /** Pentru ce soluție am adus deja arhitectura. Fără asta efectul reintra la
   *  fiecare comutare a machetei și mai lua o dată un document de 23.864 de
   *  obiecte — de fiecare dată de la zero, peste rețea. */
  const arhAdusa = useRef<string | null>(null);
  /** Bătaia care reîntreabă cât timp arhitectura nu e gata. Macheta se compune
   *  DIN DOCUMENTUL ÎNTREG, nu dintr-o copie de pe parcurs: jobul își rescrie
   *  soluția în timp ce macină, iar o copie luată devreme arăta o clădire fără
   *  ferestre — adică un model stricat, deși nimic nu era stricat. */
  const [reincearcaArh, setReincearcaArh] = useState(0);
  const ceasArh = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** REVIZIA documentului adus: id, stare, `updated_at` și revizia fațadei.
   *  Cât timp arhitectura macină, bătaia reîntreabă la 30 s — dar documentul
   *  (~20 MB) se cere din nou NUMAI dacă rândul s-a schimbat. Cerut de fiecare
   *  dată, macheta se reconstruia din temelii la fiecare bătaie, cu camera
   *  întoarsă la preset, chiar când nimic nu se schimbase (28.09.2026). Orice
   *  rescriere în loc a soluției — pasul 2, îmbrăcarea agentului de fațadă —
   *  pune `updated_at` pe rând, deci cheia se schimbă exact când se schimbă
   *  și documentul. */
  const arhCheie = useRef<string | null>(null);
  /** Soluția pentru care sunt id-ul și documentul de acum. */
  const solutiaArh = useRef<string | null>(null);
  /** Semnalul registrului la ultima căutare (v. `semnalRegistru`). */
  const semnalVazut = useRef<string | undefined>(undefined);
  useEffect(() => {
    // ALTĂ SOLUȚIE = ARHITECTURA VECHE PLEACĂ PE LOC, nu când vine cea nouă.
    // Până sosea documentul (secunde, zeci de MB), panoul de fațadă, discuția
    // asistentului și uneltele lui rămâneau pe arhitectura situației de
    // dinainte: un mesaj scris atunci pleca în discuția ei, o modificare se
    // scria pe ea (Cătălin, 21.10.2026). Numai la schimbarea soluției: bătaia
    // care reîntreabă cât macină o arhitectură trece pe aici cu același id.
    if (solutiaArh.current !== solution.id) {
      solutiaArh.current = solution.id;
      arhCheie.current = null;
      setArhId(null);
      setDocArh(null);
    }
    // REGISTRUL S-A SCHIMBAT pentru arhitecturile planului: a apărut una, sau
    // una și-a rescris rândul (agentul de fațadă) — se caută din nou
    if (semnalRegistru !== semnalVazut.current) {
      semnalVazut.current = semnalRegistru;
      arhAdusa.current = null;
    }
    if (!machetaSit || !vedeSituatia || solution.discipline !== "site_plan") return;
    if (arhAdusa.current === solution.id) return;
    arhAdusa.current = solution.id;
    let viu = true;
    let terminat = false;
    setArhSeIncarca(true);
    listSolutions(projectId)
      .then(({ solutii: lista }) => {
        // Arhitectura construită PE soluția asta, nu orice arhitectură a
        // proiectului: două planuri de situație au amprente diferite, iar
        // ferestrele uneia pe volumele celeilalte ar cădea în gol.
        // ȘI CEA ÎN LUCRU. Jobul scrie soluția cu ETAJELE gata la pasul 1/2,
        // după câteva zeci de secunde, și abia apoi umple subsolurile — minute.
        // Fațada, ferestrele și balcoanele sunt toate în etaje, deci macheta
        // nu are de ce să aștepte parcarea. Se preferă una gata, apoi cea
        // promovată, apoi cea mai nouă.
        const rang = (x: SolutieRand) =>
          (x.status === "ready" ? 2 : 0) + (x.promoted ? 1 : 0);
        const rude = lista.filter((x) => x.discipline === "architecture"
                                         && x.lineage?.site_plan === solution.id);
        const arh = rude
          .filter(areDesen)
          .sort((a, b) => rang(b) - rang(a) || b.version - a.version)[0];
        if (!arh) {
          if (viu) { setDocArh(null); setArhId(null); setArhSeIncarca(false);
                     setArhInLucru(false); }
          arhAdusa.current = null;
          arhCheie.current = null;
          return;
        }
        const revizieFatada = (arh.summary as { fatada?: { revizie?: unknown } } | undefined)
          ?.fatada?.revizie;
        const cheie = `${arh.id}|${arh.status}|${arh.updated_at ?? ""}|${revizieFatada ?? ""}`;
        // MACHETA APARE ÎNAINTEA PARCĂRII, DAR NU SE ȚINE MINTE. Etajele —
        // fațada, ferestrele, balcoanele — sunt scrise la pasul 1/2, iar
        // subsolurile durează minute: n-are rost să aștepți parcarea ca să vezi
        // clădirile. Numai că jobul rescrie ACEEAȘI soluție pe parcurs, iar o
        // regenerare poate scoate cu totul altă arhitectură — deci copia asta
        // e bună de arătat, nu de păstrat. Cât timp macină, se cere din nou la
        // fiecare bătaie (documentul crește sub tine, iar la sfârșit e cel
        // întreg), și se spune pe ecran că e în lucru.
        // `nonconvergent` e încheiată ca și `ready`: nu mai are ce crește sub tine
        const macina = arh.status === "running";
        if (viu) setArhInLucru(macina);
        if (macina) {
          arhAdusa.current = null;
          if (viu) ceasArh.current = setTimeout(
            () => setReincearcaArh((x) => x + 1), REIA_ARH_MS);
        }
        // UN INSTANTANEU PUS LA LOC de anulare/refacere e chiar documentul
        // scris: se notează cheia, nu se mai aduce și nu se reconstruiește.
        if (adoptaCheiaArh.current && arh.id === arhCheie.current?.split("|")[0]) {
          adoptaCheiaArh.current = false;
          arhCheie.current = cheie;
          if (viu) setArhSeIncarca(false);
          return;
        }
        adoptaCheiaArh.current = false;
        // ACEEAȘI REVIZIE CA CEA DE PE ECRAN: nu se mai cere, nu se mai
        // reconstruiește nimic; bătaia rămâne armată pentru următoarea.
        if (cheie === arhCheie.current) {
          if (viu) setArhSeIncarca(false);
          return;
        }
        // FĂRĂ MEMORIE. Documentul se cere de fiecare dată, întreg. Ținut,
        // o soluție refăcută sub același id și aceeași stare întorcea copia
        // veche — exact felul de fantomă pentru care nu merită cele câteva
        // secunde câștigate la o plimbare situație → arhitectură → situație.
        // CU TOATE NIVELURILE: macheta le stivuiește pe toate, deci feliile
        // `start`/`rest` ale planului 2D n-ar fi un volum, ar fi un etaj
        // plutind singur. Dar fără balastul pe care nu-l citește nimeni din
        // interfață (`compozitie_baza` al generatorului: 30 % din octeții
        // comprimați pe Demo, 23.10.2026) — felia `macheta`, v.
        // `control-plane/services/felii.py`.
        return api.solutionDocument(projectId, arh.id, { parte: "macheta" })
          .catch((e) => {
            // O ARHITECTURĂ ÎN LUCRU FĂRĂ DOCUMENT ÎNCĂ — pasul ei 1/2 n-a
            // scris etajele. Nu e o eroare: macheta rămâne cu volumele curate
            // și se mai întreabă peste `REIA_ARH_MS`, ca la o arhitectură care
            // macină. Fără asta, butonul 3D deschis devreme rămânea pe volume
            // goale până la o nouă apăsare.
            if (macina && viu) {
              setDocArh(null); setArhSeIncarca(false);
              return null;
            }
            throw e;
          })
          .then((d) => {
            if (!viu || d == null) return;
            // BRUT, NU ÎNTORS. `docArh` e `northUp(docArhBrut)` — orientarea se
            // face O SINGURĂ DATĂ, în memo, ca fațada previzualizată (care vine
            // tot brută) să treacă prin aceeași transformare. Întors și aici,
            // documentul lua sfertul de tură de două ori: pe un sistem fără
            // «northing first» cele două se anulau, deci arhitectura rămânea
            // NEÎNTOARSĂ sub un plan de situație întors — volumele curate ale
            // situației într-o parte, pielea și ferestrele arhitecturii în
            // alta. De aici clădirile fără ferestre și pereții care apăreau
            // peste terase (măsurat, 22.09.2026).
            setDocArh(d as unknown as CanonicalDoc);
            setArhId(arh.id);
            setArhSeIncarca(false);
            arhCheie.current = cheie;
          });
      })
      // Fără arhitectură macheta rămâne cu volume curate — nu e o eroare de
      // arătat, e starea normală a unui plan de situație proaspăt.
      .catch(() => {
        // Ca să se poată reîncerca la următoarea deschidere, nu să rămână
        // marcată ca adusă o soluție al cărei document n-a venit niciodată.
        arhAdusa.current = null;
        if (viu) { setDocArh(null); setArhSeIncarca(false); }
      })
      .finally(() => { terminat = true; });
    return () => {
      viu = false;
      if (ceasArh.current) { clearTimeout(ceasArh.current); ceasArh.current = null; }
      // OPRITĂ PE DRUM (macheta închisă cât documentul încă venea): soluția nu
      // rămâne marcată ca adusă, altfel redeschiderea se oprea la «adusă» și
      // macheta stătea pe «se încarcă» până la altă soluție
      if (!terminat) {
        arhAdusa.current = null;
        if (laAnulare) laAnulare(); else setArhSeIncarca(false);
      }
    };
  }, [projectId, solution.id, solution.discipline, machetaSit, vedeSituatia,
      reincearcaArh, semnalRegistru]);
  return { arhAdusa, arhCheie, setReincearcaArh };
}
